export const operationsManagementCourse = {
  id: 'operations-management',
  title: 'Operations Management',
  description: 'Comprehensive study of operations strategy, process management, supply chain optimization, quality management, and lean operations with emphasis on efficiency and continuous improvement.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  category: 'Business',
  prerequisites: ['Introduction to Business', 'Business Statistics', 'Principles of Management'],
  learningObjectives: [
    'Understand operations strategy and competitive priorities',
    'Analyze process design and capacity management',
    'Evaluate supply chain and inventory management strategies',
    'Apply quality management and lean operations principles',
    'Develop project management and scheduling skills',
    'Implement operations performance measurement systems'
  ],
  skills: [
    'Process Analysis and Design',
    'Supply Chain Management',
    'Quality Management Systems',
    'Lean Operations',
    'Project Management',
    'Inventory Optimization',
    'Capacity Planning',
    'Performance Measurement',
    'Six Sigma Methodology',
    'Operations Strategy Development'
  ],
  careerPaths: [
    'Operations Manager',
    'Supply Chain Manager',
    'Quality Manager',
    'Process Improvement Specialist',
    'Production Planner',
    'Logistics Coordinator',
    'Operations Analyst',
    'Project Manager',
    'Manufacturing Manager',
    'Operations Consultant'
  ],
  averageSalary: '$65,000 - $110,000',
  marketDemand: 'High',
  certifications: [
    'APICS Supply Chain Operations Reference (SCOR)',
    'Certified Supply Chain Professional (CSCP)',
    'Lean Six Sigma Green Belt/Black Belt',
    'Project Management Professional (PMP)',
    'Certified in Production and Inventory Management (CPIM)'
  ],
  industryApplications: [
    'Manufacturing',
    'Healthcare Operations',
    'Service Industries',
    'Logistics and Transportation',
    'Technology Companies',
    'Retail Operations',
    'Food and Beverage',
    'Consulting Services'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Operations Strategy and Process Design',
      week: 1,
      description: 'Operations strategy development, competitive priorities, and process design fundamentals',
      learningObjectives: [
        'Understand the strategic role of operations in organizational success',
        'Analyze competitive priorities and their trade-offs',
        'Design and evaluate different process types and configurations',
        'Apply process analysis tools for performance improvement'
      ],
      content: {
        type: 'lesson',
        data: {
          title: 'Strategic Operations Management and Process Optimization',
          content: `# Strategic Operations Management and Process Optimization

## Operations Strategy Fundamentals

### Definition and Scope
Operations strategy is the long-term plan for using organizational resources to achieve competitive advantage through operations. It encompasses decisions about:

- **Capacity**: How much and when to expand
- **Facilities**: Where to locate and how to arrange
- **Technology**: What processes and equipment to use
- **Vertical Integration**: What to make versus buy
- **Workforce**: How to organize and motivate people

### Strategic Role of Operations

**Operations as a Competitive Weapon**
Operations can provide sustainable competitive advantage through:

1. **Cost Leadership**: Achieving lowest cost position in industry
   - Economies of scale and scope
   - Learning curve effects
   - Process efficiency improvements
   - Supply chain optimization

2. **Differentiation**: Creating unique value for customers
   - Superior quality and reliability
   - Customization and flexibility
   - Innovation and new product development
   - Exceptional service delivery

3. **Focus Strategy**: Targeting specific market segments
   - Niche market specialization
   - Tailored operations capabilities
   - Deep customer understanding
   - Specialized expertise development

## Competitive Priorities Framework

### The Four Core Priorities

**1. Cost Priority**
- **Low-Cost Operations**: Minimizing total cost per unit
- **Cost Reduction**: Continuous improvement in efficiency
- **Economies of Scale**: Leveraging volume for cost advantage
- **Lean Operations**: Eliminating waste and non-value activities

*Example*: Walmart's focus on everyday low prices through efficient supply chain and operations

**2. Quality Priority**
- **Performance Quality**: Meeting or exceeding specifications
- **Conformance Quality**: Consistency and reliability
- **Total Quality Management**: Company-wide quality focus
- **Quality at the Source**: Prevention over inspection

*Example*: Toyota's reputation for reliability and quality through systematic quality management

**3. Speed Priority**
- **Delivery Speed**: Fast order fulfillment
- **Development Speed**: Rapid new product introduction
- **Response Speed**: Quick reaction to market changes
- **Process Speed**: Efficient internal operations

*Example*: Amazon's same-day and next-day delivery capabilities

**4. Flexibility Priority**
- **Product Flexibility**: Ability to handle product variety
- **Volume Flexibility**: Capacity to handle demand fluctuations
- **Process Flexibility**: Adaptable production systems
- **Service Flexibility**: Customizable service offerings

*Example*: Dell's mass customization allowing customer configuration

### Priority Trade-offs and Alignment

**Traditional Trade-off Model**
- Excellence in one priority often means compromise in others
- Organizations must choose primary competitive priority
- Resource allocation reflects strategic choices
- Clear focus prevents operational confusion

**Modern Perspective: Order Winners vs. Order Qualifiers**
- **Order Qualifiers**: Minimum performance standards to compete
- **Order Winners**: Performance dimensions that win orders
- **Dynamic Nature**: Priorities change over product/service lifecycle
- **Cumulative Model**: Build capabilities progressively

## Operations Strategy Framework

### Hayes and Wheelwright Model

**Stage 1: Internally Neutral**
- Operations seen as necessary evil
- Minimize negative impact on company
- Focus on avoiding mistakes
- Limited strategic contribution

**Stage 2: Externally Neutral**
- Operations follows industry best practices
- Benchmarking and copying competitors
- Achieving parity performance
- No competitive advantage from operations

**Stage 3: Internally Supportive**
- Operations supports business strategy
- Clear linkage between operations and strategy
- Consistent operational policies
- Credible operations function

**Stage 4: Externally Supportive**
- Operations drives competitive advantage
- Operations capabilities shape strategy
- Industry leadership in operations
- Source of sustainable advantage

### Strategic Fit Assessment

**Market Requirements Analysis**
- Customer needs and expectations
- Competitive landscape evaluation
- Market trends and future direction
- Regulatory and environmental factors

**Operations Capabilities Audit**
- Current performance assessment
- Resource and capability evaluation
- Technology and process analysis
- Organizational competencies review

**Gap Analysis and Action Planning**
- Performance gap identification
- Priority setting for improvements
- Resource allocation decisions
- Implementation timeline development

## Process Design and Management

### Process Types and Characteristics

**1. Job Shop Process**
- **Characteristics**: High variety, low volume, skilled workers
- **Layout**: Functional (process) layout
- **Equipment**: General purpose, flexible
- **Examples**: Custom furniture, machine shop, hospital
- **Advantages**: High flexibility, skilled workforce
- **Disadvantages**: High cost, complex scheduling

**2. Batch Process**
- **Characteristics**: Medium variety, medium volume
- **Layout**: Cellular or functional
- **Equipment**: Semi-dedicated
- **Examples**: Bakery, pharmaceutical, textiles
- **Advantages**: Economies of scale, flexibility
- **Disadvantages**: Setup time, inventory

**3. Assembly Line Process**
- **Characteristics**: Low variety, high volume
- **Layout**: Product layout
- **Equipment**: Dedicated, specialized
- **Examples**: Automotive, electronics, appliances
- **Advantages**: High efficiency, low cost
- **Disadvantages**: Limited flexibility, high capital

**4. Continuous Process**
- **Characteristics**: Very high volume, standardized
- **Layout**: Fixed position or continuous flow
- **Equipment**: Highly specialized, automated
- **Examples**: Chemicals, petroleum, steel
- **Advantages**: Lowest cost, high efficiency
- **Disadvantages**: Very limited flexibility

### Process Selection Factors

**Volume and Variety Analysis**
- Product/service volume requirements
- Variety and customization needs
- Demand stability and predictability
- Customer expectations for delivery

**Economic Considerations**
- Capital investment requirements
- Operating cost implications
- Break-even analysis
- Return on investment

**Strategic Alignment**
- Fit with competitive priorities
- Support for business strategy
- Technology requirements
- Workforce implications

## Process Analysis and Improvement Tools

### Process Flow Analysis

**Process Flow Charting**
- Visual representation of process steps
- Decision points and alternative paths
- Input and output identification
- Responsibility and handoff points

**Value Stream Mapping**
- End-to-end process visualization
- Value-added vs. non-value-added activities
- Information and material flows
- Lead time and cycle time analysis

### Capacity Analysis

**Theoretical Capacity**
- Maximum possible output under ideal conditions
- 24/7 operation without any delays
- Benchmark for efficiency measurement

**Practical Capacity**
- Realistic maximum considering normal operations
- Includes routine maintenance and breaks
- Typical operating efficiency levels

**Effective Capacity**
- Expected output given product mix and schedules
- Considers setup times and changeovers
- Reflects actual operating conditions

### Bottleneck Analysis

**Theory of Constraints Principles**
- System performance limited by weakest link
- Focus improvement efforts on constraints
- Elevate constraint performance
- Subordinate non-constraints to constraints

**Bottleneck Identification Methods**
- Capacity utilization analysis
- Queue length observation
- Cycle time measurement
- Work-in-process accumulation

### Process Performance Metrics

**Efficiency Measures**
- Utilization: Actual output / Designed capacity
- Efficiency: Actual output / Effective capacity
- Productivity: Output / Input ratio
- Overall Equipment Effectiveness (OEE)

**Quality Measures**
- Defect rates and first-pass yield
- Rework and scrap costs
- Customer satisfaction scores
- Process capability indices

**Time Measures**
- Cycle time and lead time
- Setup and changeover time
- Queue time and waiting time
- On-time delivery performance

## Contemporary Operations Strategy Issues

### Digital Transformation
- Industry 4.0 and smart manufacturing
- Internet of Things (IoT) integration
- Artificial intelligence and machine learning
- Digital twin technology

### Sustainability and Circular Economy
- Environmental impact reduction
- Resource efficiency and waste minimization
- Sustainable supply chain design
- Circular business models

### Resilience and Risk Management
- Supply chain disruption preparedness
- Business continuity planning
- Flexible and adaptable operations
- Risk assessment and mitigation

### Servitization Trends
- Product-service system design
- Outcome-based business models
- Digital service integration
- Customer experience focus`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Process Efficiency Calculator',
          description: 'Analyze process efficiency and identify bottlenecks',
          exerciseData: {
            type: 'process_analysis',
            scenario: 'Manufacturing Line Optimization',
            metrics: ['cycle_time', 'throughput', 'utilization', 'efficiency']
          }
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Supply Chain and Inventory Management',
      week: 4,
      description: 'Supply chain strategy, vendor management, and inventory optimization techniques',
      content: {
        type: 'lesson',
        data: {
          title: 'Supply Chain Optimization and Inventory Control',
          content: `# Supply Chain Optimization and Inventory Control

## Supply Chain Management

### Supply Chain Strategy
- **Supplier Selection**: Criteria and evaluation
- **Supplier Relationships**: Partnership development
- **Supply Chain Integration**: Information sharing
- **Risk Management**: Supply disruption mitigation

### Inventory Management
- **Economic Order Quantity (EOQ)**: Optimal order size
- **ABC Analysis**: Inventory classification
- **Just-in-Time (JIT)**: Lean inventory approach
- **Safety Stock**: Buffer inventory management

## Demand Forecasting
- **Qualitative Methods**: Expert judgment, market research
- **Quantitative Methods**: Time series, causal models
- **Forecast Accuracy**: Error measurement and improvement
- **Collaborative Planning**: Demand planning integration`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Inventory Optimization Calculator',
          description: 'Calculate optimal inventory levels and order quantities',
          exerciseData: {
            type: 'inventory_optimization',
            scenario: 'Multi-Product Inventory Management',
            parameters: {
              demand: 1000,
              orderCost: 50,
              holdingCost: 5,
              leadTime: 7
            }
          }
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Quality Management and Lean Operations',
      week: 7,
      description: 'Total quality management, statistical process control, and lean manufacturing principles',
      content: {
        type: 'lesson',
        data: {
          title: 'Quality Excellence and Lean Operations Implementation',
          content: `# Quality Excellence and Lean Operations Implementation

## Total Quality Management (TQM)

### Quality Principles
- **Customer Focus**: Meeting customer requirements
- **Continuous Improvement**: Kaizen philosophy
- **Employee Involvement**: Quality responsibility
- **Supplier Partnerships**: Quality collaboration

### Quality Tools
- **Statistical Process Control (SPC)**: Process monitoring
- **Control Charts**: Variation tracking
- **Pareto Analysis**: Problem prioritization
- **Root Cause Analysis**: Problem solving

## Lean Operations

### Lean Principles
- **Value Identification**: Customer value focus
- **Value Stream Mapping**: Process flow optimization
- **Flow Creation**: Smooth operations
- **Pull Systems**: Demand-driven production
- **Perfection Pursuit**: Continuous improvement

### Lean Tools
- **5S Methodology**: Workplace organization
- **Kanban Systems**: Visual flow control
- **Poka-Yoke**: Error prevention
- **SMED**: Quick changeover techniques`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Quality Cost Calculator',
          description: 'Calculate and analyze cost of quality components',
          exerciseData: {
            type: 'quality_cost_analysis',
            scenario: 'Quality Improvement Program',
            categories: ['prevention', 'appraisal', 'internal_failure', 'external_failure']
          }
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Capacity Planning and Scheduling',
      week: 11,
      description: 'Capacity management, production scheduling, and resource optimization',
      content: {
        type: 'lesson',
        data: {
          title: 'Capacity Management and Production Scheduling',
          content: `# Capacity Management and Production Scheduling

## Capacity Planning

### Capacity Strategy
- **Capacity Measurement**: Effective capacity calculation
- **Capacity Utilization**: Efficiency optimization
- **Capacity Expansion**: Growth planning
- **Capacity Flexibility**: Adaptability requirements

### Scheduling Techniques
- **Gantt Charts**: Timeline visualization
- **Critical Path Method (CPM)**: Project scheduling
- **Program Evaluation Review Technique (PERT)**: Probabilistic scheduling
- **Loading and Sequencing**: Resource allocation

## Resource Management
- **Workforce Planning**: Staffing optimization
- **Equipment Utilization**: Asset efficiency
- **Facility Layout**: Spatial optimization
- **Technology Integration**: Automation benefits`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Capacity Planning Calculator',
          description: 'Calculate optimal capacity and utilization rates',
          exerciseData: {
            type: 'capacity_planning',
            scenario: 'Production Capacity Analysis',
            variables: ['demand_forecast', 'current_capacity', 'utilization_rate', 'expansion_cost']
          }
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Operations Performance and Technology',
      week: 14,
      description: 'Performance measurement, operations analytics, and technology integration',
      content: {
        type: 'lesson',
        data: {
          title: 'Operations Analytics and Digital Transformation',
          content: `# Operations Analytics and Digital Transformation

## Performance Measurement

### Key Performance Indicators (KPIs)
- **Productivity Measures**: Output per input
- **Quality Metrics**: Defect rates, customer satisfaction
- **Cost Measures**: Unit costs, cost reduction
- **Time Metrics**: Cycle time, delivery performance

### Balanced Scorecard
- **Financial Perspective**: Cost and profitability
- **Customer Perspective**: Satisfaction and loyalty
- **Internal Process**: Efficiency and quality
- **Learning and Growth**: Innovation and improvement

## Technology in Operations

### Industry 4.0
- **Internet of Things (IoT)**: Connected devices
- **Artificial Intelligence**: Predictive analytics
- **Robotics and Automation**: Process automation
- **Big Data Analytics**: Data-driven decisions

### Digital Operations
- **Enterprise Resource Planning (ERP)**: Integrated systems
- **Supply Chain Management Systems**: End-to-end visibility
- **Manufacturing Execution Systems (MES)**: Production control
- **Predictive Maintenance**: Equipment optimization`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Operations Dashboard',
          description: 'Monitor and analyze key operations performance metrics',
          exerciseData: {
            type: 'operations_dashboard',
            scenario: 'Manufacturing Performance Monitoring',
            kpis: ['oee', 'quality_rate', 'delivery_performance', 'cost_variance']
          }
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Operations Strategy Analysis',
      description: 'Analyze operations strategy of a selected company',
      week: 3,
      points: 150,
      requirements: [
        'Select a manufacturing or service company',
        'Analyze current operations strategy and competitive priorities',
        'Identify improvement opportunities',
        'Recommend strategic changes with justification',
        'Present findings in comprehensive report (8-10 pages)',
        'Include process flow diagrams and analysis',
        'Cite minimum 8 academic sources'
      ],
      rubric: {
        'Company Analysis': 30,
        'Strategic Assessment': 35,
        'Improvement Recommendations': 40,
        'Professional Presentation': 25,
        'Use of Course Concepts': 20
      }
    },
    {
      id: 'assignment-2',
      title: 'Supply Chain Optimization Project',
      description: 'Design and optimize a supply chain for a specific industry',
      week: 6,
      points: 175,
      requirements: [
        'Choose a product and analyze its supply chain',
        'Map current state value stream',
        'Identify waste and inefficiencies',
        'Calculate inventory costs and EOQ',
        'Design improved supply chain with justification',
        'Present cost-benefit analysis',
        'Create implementation timeline'
      ],
      rubric: {
        'Current State Analysis': 35,
        'Quantitative Analysis': 40,
        'Improvement Design': 45,
        'Cost-Benefit Analysis': 30,
        'Implementation Plan': 25
      }
    },
    {
      id: 'assignment-3',
      title: 'Quality Improvement Case Study',
      description: 'Implement quality management principles to solve operational problems',
      week: 10,
      points: 150,
      requirements: [
        'Identify quality problem in real organization',
        'Apply appropriate quality tools for analysis',
        'Design quality improvement plan',
        'Calculate cost of quality',
        'Propose control mechanisms',
        'Develop training recommendations'
      ],
      rubric: {
        'Problem Identification': 25,
        'Quality Tools Application': 40,
        'Improvement Plan': 35,
        'Cost Analysis': 30,
        'Control Systems': 20
      }
    },
    {
      id: 'assignment-4',
      title: 'Lean Operations Implementation',
      description: 'Design lean transformation plan for operational efficiency',
      week: 13,
      points: 175,
      requirements: [
        'Conduct current state assessment',
        'Identify waste types and sources',
        'Apply lean tools and techniques',
        'Design future state operations',
        'Calculate efficiency improvements',
        'Create change management plan',
        'Present to stakeholders'
      ],
      rubric: {
        'Current State Assessment': 30,
        'Waste Identification': 35,
        'Lean Tools Application': 40,
        'Future State Design': 35,
        'Change Management': 25,
        'Presentation Quality': 10
      }
    }
  ],
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Operations Strategy and Process Design Quiz',
      description: 'Assessment of operations strategy concepts and process analysis',
      week: 2,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'multiple_choice',
          question: 'Which competitive priority focuses on reducing production costs?',
          options: ['Quality', 'Speed', 'Cost', 'Flexibility'],
          correct: 2,
          explanation: 'Cost as a competitive priority focuses on achieving low-cost operations and production efficiency.'
        },
        {
          type: 'multiple_choice',
          question: 'What type of process is characterized by high variety and low volume?',
          options: ['Assembly Line', 'Continuous Process', 'Job Shop', 'Batch Process'],
          correct: 2,
          explanation: 'Job shops handle high variety, low volume production with flexible processes.'
        },
        {
          type: 'short_answer',
          question: 'Explain the concept of process capacity and how it affects operations performance.',
          points: 10,
          expectedLength: '3-4 sentences'
        }
      ]
    },
    {
      id: 'quiz-2',
      title: 'Supply Chain and Inventory Management Quiz',
      description: 'Assessment of supply chain concepts and inventory optimization',
      week: 5,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'calculation',
          question: 'Calculate EOQ given: Annual demand = 2000 units, Order cost = $50, Holding cost = $5 per unit per year',
          correct: 200,
          explanation: 'EOQ = √(2DS/H) = √(2×2000×50/5) = √40000 = 200 units',
          formula: 'EOQ = √(2DS/H)'
        },
        {
          type: 'multiple_choice',
          question: 'Which inventory classification system categorizes items by value and usage?',
          options: ['EOQ', 'ABC Analysis', 'JIT', 'MRP'],
          correct: 1,
          explanation: 'ABC Analysis classifies inventory items based on their annual dollar volume (value × usage).'
        }
      ]
    },
    {
      id: 'quiz-3',
      title: 'Quality Management and Lean Operations Quiz',
      description: 'Assessment of quality principles and lean methodologies',
      week: 8,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'multiple_choice',
          question: 'Which lean principle focuses on customer value identification?',
          options: ['Flow', 'Pull', 'Value', 'Perfection'],
          correct: 2,
          explanation: 'Value identification is the first lean principle, focusing on understanding customer value.'
        },
        {
          type: 'true_false',
          question: 'Statistical Process Control charts are used to monitor process variation over time.',
          correct: true,
          explanation: 'SPC charts track process performance and identify when processes are out of control.'
        }
      ]
    },
    {
      id: 'quiz-4',
      title: 'Capacity Planning and Scheduling Quiz',
      description: 'Assessment of capacity management and scheduling techniques',
      week: 12,
      points: 50,
      timeLimit: 45,
      questions: [
        {
          type: 'calculation',
          question: 'If a facility operates 8 hours/day, 5 days/week, with 85% efficiency, what is the effective weekly capacity?',
          correct: 34,
          explanation: 'Effective capacity = 8 hours × 5 days × 0.85 = 34 hours per week',
          showWork: true
        },
        {
          type: 'multiple_choice',
          question: 'Which scheduling technique uses network diagrams to show project dependencies?',
          options: ['Gantt Chart', 'CPM', 'Loading', 'Sequencing'],
          correct: 1,
          explanation: 'Critical Path Method (CPM) uses network diagrams to show task dependencies and critical path.'
        }
      ]
    }
  ],
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Operations Management Examination',
    description: 'Assessment covering all operations management concepts and applications',
    week: 16,
    points: 200,
    sections: [
      {
        name: 'Operations Strategy and Process Design',
        points: 50,
        topics: ['Competitive priorities', 'Process analysis', 'Capacity planning', 'Facility layout']
      },
      {
        name: 'Supply Chain and Inventory Management',
        points: 50,
        topics: ['Supply chain strategy', 'Inventory models', 'Supplier management', 'Demand forecasting']
      },
      {
        name: 'Quality and Lean Operations',
        points: 50,
        topics: ['TQM principles', 'Statistical quality control', 'Lean tools', 'Continuous improvement']
      },
      {
        name: 'Performance and Technology',
        points: 50,
        topics: ['Performance measurement', 'Operations analytics', 'Technology integration', 'Digital transformation']
      }
    ]
  },
  finalProject: {
    id: 'final-project',
    title: 'Comprehensive Operations Transformation Project',
    description: 'Design and present a complete operations transformation plan for a real organization',
    week: 15,
    points: 300,
    duration: '6 weeks',
    requirements: [
      'Select a real organization (manufacturing or service)',
      'Conduct comprehensive operations assessment',
      'Analyze current operations strategy and performance',
      'Identify improvement opportunities across all functional areas',
      'Design integrated improvement plan using course concepts',
      'Calculate ROI and implementation timeline',
      'Present recommendations to executive panel',
      'Submit comprehensive written report (25-30 pages)',
      'Include financial analysis and risk assessment'
    ],
    deliverables: [
      {
        name: 'Project Proposal',
        week: 10,
        points: 25,
        description: 'Organization selection and preliminary analysis plan'
      },
      {
        name: 'Current State Assessment',
        week: 12,
        points: 50,
        description: 'Detailed analysis of current operations'
      },
      {
        name: 'Improvement Plan Design',
        week: 14,
        points: 75,
        description: 'Comprehensive transformation strategy'
      },
      {
        name: 'Final Presentation',
        week: 15,
        points: 75,
        description: '20-minute executive presentation'
      },
      {
        name: 'Written Report',
        week: 15,
        points: 75,
        description: 'Comprehensive documentation and analysis'
      }
    ],
    rubric: {
      'Operations Analysis': 60,
      'Strategic Thinking': 50,
      'Quantitative Analysis': 55,
      'Implementation Planning': 45,
      'Professional Communication': 40,
      'Use of Course Concepts': 50
    },
    learningOutcomes: [
      'Integrate operations management concepts into practical solutions',
      'Demonstrate analytical and problem-solving skills',
      'Apply quantitative methods to operations decisions',
      'Communicate complex ideas to executive audiences',
      'Develop strategic thinking and implementation planning'
    ]
  },
  gradingBreakdown: {
    participation: { weight: 10, description: 'Class participation and engagement' },
    quizzes: { weight: 20, description: 'Four module quizzes' },
    assignments: { weight: 30, description: 'Four major assignments' },
    finalExam: { weight: 20, description: 'Comprehensive final examination' },
    finalProject: { weight: 20, description: 'Operations transformation project' }
  },
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D+': '67-69%',
    'D': '60-66%',
    'F': 'Below 60%'
  },
  courseSchedule: {
    'Week 1': 'Course Introduction and Operations Strategy',
    'Week 2': 'Process Design and Analysis',
    'Week 3': 'Operations Strategy Assignment Due',
    'Week 4': 'Supply Chain Management Fundamentals',
    'Week 5': 'Inventory Management and Control',
    'Week 6': 'Supply Chain Project Due',
    'Week 7': 'Quality Management Principles',
    'Week 8': 'Statistical Quality Control',
    'Week 9': 'Lean Operations and Waste Elimination',
    'Week 10': 'Quality Improvement Case Study Due / Project Proposal Due',
    'Week 11': 'Capacity Planning and Management',
    'Week 12': 'Production Scheduling / Current State Assessment Due',
    'Week 13': 'Lean Implementation Assignment Due',
    'Week 14': 'Operations Performance and Analytics / Improvement Plan Due',
    'Week 15': 'Final Project Presentations',
    'Week 16': 'Final Examination'
  },
  coursePolicies: {
    attendance: {
      policy: 'Regular attendance is essential for success in this course. More than two unexcused absences may result in grade reduction.',
      makeupPolicy: 'Makeup exams and extended deadlines require advance notice and documentation of emergency circumstances.'
    },
    lateSubmissions: {
      policy: 'Late assignments will be penalized 10% per day unless prior arrangements are made.',
      exceptions: 'Extensions may be granted for documented emergencies or health issues.'
    },
    academicIntegrity: {
      policy: 'All work must be original. Collaboration is encouraged on assignments but individual submission required.',
      plagiarism: 'Plagiarism will result in course failure. Proper citation required for all sources.',
      examPolicy: 'Exams are individual efforts. No collaboration or external resources permitted.'
    },
    technology: {
      laptops: 'Laptops permitted for note-taking and course activities',
      phones: 'Mobile phones should be silenced during class',
      software: 'Students will use Excel, process mapping software, and statistical tools'
    },
    communication: {
      email: 'Allow 24-48 hours for email responses during business days',
      officeHours: 'Tuesday/Thursday 2-4 PM or by appointment',
      announcements: 'Check course management system daily for updates'
    }
  },
  learningSupport: {
    tutoring: 'Free tutoring available through Academic Success Center',
    accommodations: 'Students with disabilities should contact Disability Services',
    resources: [
      'Operations management simulation software',
      'Statistical analysis tools',
      'Case study database access',
      'Industry report library'
    ]
  },
  resources: [
    {
      type: 'textbook',
      title: 'Operations Management: Processes and Supply Chains',
      author: 'Lee Krajewski, Manoj Malhotra, Larry Ritzman',
      edition: '12th Edition',
      isbn: '978-0134741062',
      required: true
    },
    {
      type: 'textbook',
      title: 'Operations Management: Sustainability and Supply Chain Management',
      author: 'Jay Heizer, Barry Render, Chuck Munson',
      edition: '13th Edition',
      isbn: '978-0135173626',
      required: false,
      purpose: 'Supplementary reading'
    },
    {
      type: 'software',
      title: 'Microsoft Excel with Solver Add-in',
      required: true,
      purpose: 'Quantitative analysis and optimization'
    },
    {
      type: 'software',
      title: 'Lucidchart or Visio',
      required: false,
      purpose: 'Process mapping and flow charting'
    },
    {
      type: 'database',
      title: 'Harvard Business Review Case Collection',
      access: 'University subscription',
      purpose: 'Case studies and current articles'
    },
    {
      type: 'journal',
      title: 'Journal of Operations Management',
      access: 'University library',
      purpose: 'Current research and trends'
    },
    {
      type: 'website',
      title: 'APICS Supply Chain Operations Reference (SCOR) Model',
      url: 'https://www.apics.org/apics-for-business/frameworks/scor',
      purpose: 'Industry best practices'
    },
    {
      type: 'video',
      title: 'MIT OpenCourseWare Operations Management Lectures',
      access: 'Free online',
      purpose: 'Additional lecture content'
    }
  ],
  prerequisites_detail: {
    'Introduction to Business': 'Understanding of basic business functions and terminology',
    'Business Statistics': 'Proficiency in descriptive statistics, probability, and hypothesis testing',
    'Principles of Management': 'Knowledge of management functions, organizational behavior, and leadership'
  },
  courseObjectives: {
    knowledge: [
      'Understand strategic role of operations in organizational success',
      'Comprehend principles of process design and capacity management',
      'Master supply chain and inventory management concepts',
      'Learn quality management and continuous improvement methodologies'
    ],
    skills: [
      'Analyze operational processes and identify improvement opportunities',
      'Apply quantitative methods to operations decision making',
      'Design efficient supply chain and inventory systems',
      'Implement quality management and lean operations principles'
    ],
    attitudes: [
      'Appreciate importance of operations excellence in competitive advantage',
      'Develop systems thinking approach to problem solving',
      'Foster continuous improvement mindset',
      'Value data-driven decision making in operations'
    ]
  }
};